/**
 * The court replies of a case, on the case page.
 *
 * A case opened with the papers of a court matter — the Original Application,
 * a rejoinder, the office's own file — had no way from this page to the reply
 * those papers were uploaded for: the page asked for the complaint (O.A. 309
 * walked through the screens, 4 Oct 2026). This is that way in.
 *
 * Drawn only when the file holds a court paper or a court reply has been
 * drafted, so a case that never reaches a tribunal never sees it. Every
 * sentence and every document name is the server's; this decides nothing about
 * the stage or the next action, which stay with the card below it.
 *
 * A document whose pleading is not on the file is still listed and still opens
 * (rule 3): its workspace is where that pleading is uploaded.
 */

import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { CourtDocumentRow } from '../../api/client'

const OPEN_WORDS: Record<CourtDocumentRow['draft'], string> = {
  none: 'Open and draft',
  drafting: 'See progress',
  stopped: 'Open and continue',
  drafted: 'Open the draft',
}

export function CourtDocuments({
  caseId,
  hasGist = true,
}: {
  caseId: string
  /** The case already carries a gist (it is shown with his particulars). */
  hasGist?: boolean
}) {
  const court = useQuery({
    queryKey: ['court-documents', caseId],
    queryFn: () => api.readCourtDocuments(caseId),
    // A draft is written in the background; the line follows it.
    refetchInterval: (query) =>
      (query.state.data?.documents ?? []).some((row) => row.draft === 'drafting')
        ? 15_000
        : false,
  })
  const data = court.data
  // A court case confirmed before the gist was read at Confirm has none on its
  // record. It is read here once and shown as the app's reading — not written.
  const reading = useQuery({
    queryKey: ['court-particulars', caseId],
    queryFn: () => api.readCourtParticulars(caseId),
    enabled: !hasGist && Boolean(data && data.court_papers > 0),
    staleTime: Infinity,
    retry: false,
  })
  if (!data) return null
  const documents = data.documents ?? []
  const unnamed = data.unnamed_pleadings ?? []
  const started = documents.some((row) => row.draft !== 'none' || row.standing !== 'none')
  if (data.court_papers === 0 && !started) return null
  return (
    <section
      className="court-documents"
      data-testid="court-documents"
      aria-label="Court documents"
    >
      <h3>
        {/* The stage's own name, off the case's process version. */}
        {data.stage_title ?? 'Court documents'}{' '}
        <span className="cnt mono">
          {data.court_papers} court {data.court_papers === 1 ? 'paper' : 'papers'} on the file
        </span>
      </h3>
      {!hasGist && reading.data?.gist ? (
        <p className="small" data-testid="court-gist">
          {reading.data.gist}{' '}
          <span className="muted">
            — as the app read it from the pleading; not on the case record.
          </span>
        </p>
      ) : null}
      <ul>
        {documents.map((row) => {
          const files = row.pleading_files ?? []
          const settled = row.standing === 'uploaded' || row.standing === 'signed'
          const ready = !settled && (files.length > 0 || row.draft !== 'none')
          return (
            <li key={row.document_type} data-testid={`court-${row.document_type}`}>
              <span className="says">
                {row.number ? <span className="mono">{row.number} </span> : null}
                {row.title}
              </span>
              <span className="to-do">
                {row.says}
                {files.length > 0 && !settled ? (
                  <span className="mono"> {files.join(', ')}</span>
                ) : null}
              </span>
              <span className="act">
                <Link
                  className={ready ? 'btn primary' : 'btn ghost'}
                  to={`/cases/${caseId}/documents/${row.document_type}`}
                >
                  {settled
                    ? 'Open'
                    : ready
                      ? OPEN_WORDS[row.draft]
                      : 'Open and upload its papers'}
                </Link>
              </span>
            </li>
          )
        })}
      </ul>
      {documents.some((row) => row.skipped_on_the_rail) ? (
        /* Said once, not on every row: the rail below still marks these as not
           on the path, with its own reason and its own Record it, until the
           filing is on the record. Every row here opens either way. */
        <p className="small muted" data-testid="court-skipped">
          Under “The documents” further down, these read “not on the path” until the filing is
          recorded there (“Record it”). They can be drafted from here either way.
        </p>
      ) : null}
      {unnamed.length > 0 ? (
        <p className="small muted" data-testid="court-unnamed">
          {unnamed.length === 1 ? 'One pleading' : 'Some pleadings'} on the file
          {unnamed.length === 1 ? ' does' : ' do'} not say which pleading{' '}
          {unnamed.length === 1 ? 'it is' : 'they are'}:{' '}
          <span className="mono">{unnamed.join(', ')}</span>. Open the reply it belongs to and
          choose it there as the paper to answer.
        </p>
      ) : null}
    </section>
  )
}
